import React, { useEffect, useState } from 'react';

import { Check, Layers, X } from 'lucide-react';
import type { IControl } from 'mapbox-gl';
import { createPortal } from 'react-dom';
import { useControl } from 'react-map-gl';

import { MAP_BOX_KEY } from '../../config/config';
import {
  EXPLORE_MAP_STYLES,
  ExploreMapStyleId,
  mapStyleThumbnailUrl,
} from '../../config/exploreMapStyles';

interface MapStyleControlProps {
  isOpen: boolean;
  onClick: () => void;
}

// An empty Mapbox control: Mapbox owns the box and where it sits, React draws into it through a
// portal. `mapboxgl-ctrl-group` gives it the same dark plate as the locate button (index.css).
class PortalControl implements IControl {
  readonly container: HTMLDivElement = document.createElement('div');

  onAdd(): HTMLElement {
    this.container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
    return this.container;
  }

  onRemove(): void {
    this.container.remove();
  }
}

// The map type button, stacked just above the locate button (the client's call, 2026-11-04).
// Render it inside <Map>, after <GeolocateControl>: Mapbox puts each new bottom-corner control
// above the ones already there, so the order of the two elements is the order on screen.
//
// A real Mapbox control rather than a button floated over the map, so it rides with the locate
// button for free: index.css lifts that corner above the phone strip and slides it clear of the
// detail drawer, and a floated button would need its own copy of both rules.
export const MapStyleControl: React.FC<MapStyleControlProps> = ({
  isOpen,
  onClick,
}) => {
  const control = useControl(() => new PortalControl(), {
    position: 'bottom-right',
  });

  return createPortal(
    <button
      type="button"
      onClick={onClick}
      aria-expanded={isOpen}
      aria-label="Choose the map type"
      title="Choose the map type"
      // Mapbox's `.mapboxgl-ctrl-group button` rule sets display:block and a 29px square, and it
      // outranks a single Tailwind class, so the centring is inline. Open, it takes the gold fill
      // (`!` for the same reason), with black on it per BRAND.md.
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      className={isOpen ? '!bg-primary text-secondary' : 'text-primary'}
    >
      <Layers className="h-4 w-4" />
    </button>,
    control.container,
  );
};

interface MapStyleDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  value: ExploreMapStyleId;
  onChange: (next: ExploreMapStyleId) => void;
}

// Which map the visitor is looking at: the brand one, photos, or terrain.
//
// A drawer across the bottom rather than the dropdown panel it started as, and each choice is a
// picture of itself rather than an icon and a line of text. The words were doing work a picture
// does better: "Outdoors, terrain and contours" is a description of something the visitor could
// simply be shown. Google's own map-type sheet is this shape, so it is one a visitor has met.
//
// Bottom, not the right-hand side the filter and detail drawers use. It is three tiles in a row
// and nothing else, so it needs width and almost no height, and coming up from the bottom leaves
// the map above it visible while the choice is made.
//
// Same shell rules as CourseFilterDrawer: not components/common/Drawer.tsx (that one is modal and
// its backdrop swallows map panning), and it stays mounted and parks off-screen under a transform
// so it animates both ways, with aria-hidden and pointer-events keeping it out of reach shut.
const MapStyleDrawer: React.FC<MapStyleDrawerProps> = ({
  isOpen,
  onClose,
  value,
  onChange,
}) => {
  // The thumbnails are three Static Images API requests, and this drawer is mounted from the
  // moment the page loads. Holding them back until the first open keeps them off the critical
  // path for every visitor who never opens it, which is most of them. Once true it stays true, so
  // reopening does not re-request: the browser has them cached by then anyway.
  const [hasOpened, setHasOpened] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setHasOpened(true);
    }
  }, [isOpen]);

  // Escape closes it, same as every other drawer on this page. Only bound while it is up.
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return (): void => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  return (
    <aside
      aria-hidden={!isOpen}
      aria-label="Choose the map type"
      // z-[55] matches the filter drawer, which puts it over the map and its controls but under
      // nothing else on this page.
      className={`fixed inset-x-0 bottom-0 z-[55] border-t border-primary/20 bg-charcoal shadow-2xl transition-transform duration-300 ease-in-out ${
        isOpen ? 'translate-y-0' : 'pointer-events-none translate-y-full'
      }`}
    >
      <div className="flex items-center justify-between px-4 pb-1 pt-4">
        <h2 className="flex items-center gap-2 text-base font-semibold text-white">
          <Layers className="h-4 w-4 text-primary" />
          Map type
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close the map type picker"
          className="flex h-8 w-8 items-center justify-center rounded-full text-white/60 transition-colors hover:bg-white/10 hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Three across at every width. They are small enough to stay tappable on a 375px phone
          (about 105px each after the gaps), and a row of three is the shape that says "pick one
          of these" without a word of instruction. pb-6 leaves room for a phone's home indicator,
          which sits over the bottom of the sheet.
          max-w-md is what keeps a desktop tile near the 160px the thumbnail is drawn at. Left to
          fill the width, three tiles on a wide screen are 400px each, the sheet eats half the
          map, and the baked-in place names are blown up with them. */}
      <div className="mx-auto grid w-full max-w-md grid-cols-3 gap-3 px-4 pb-6 pt-2">
        {EXPLORE_MAP_STYLES.map((style) => {
          const isActive = style.id === value;

          return (
            <button
              key={style.id}
              type="button"
              role="radio"
              aria-checked={isActive}
              onClick={() => {
                onChange(style.id);
                // Shut on pick. The map redraws underneath, and that is the answer — a sheet left
                // open sits on top of the thing the visitor just asked to see.
                onClose();
              }}
              className="group flex flex-col items-center gap-2 focus-visible:outline-none"
            >
              {/* The ring is outside the tile rather than a border on it, so the picture is the
                  same size whether or not it is the chosen one. A border would shrink it by 2px
                  and shift the row every time the choice changed. */}
              <span
                className={`relative block aspect-[4/3] w-full overflow-hidden rounded-xl bg-white/5 ring-2 transition-[box-shadow,--tw-ring-color] ${
                  isActive
                    ? 'ring-primary'
                    : 'ring-white/15 group-hover:ring-primary/60'
                }`}
              >
                {/* Held back until the drawer has been opened once — see hasOpened. The alt is
                    empty because the label under it already names the tile, and a screen reader
                    reading "Satellite" twice per choice is noise. If the image fails (no token,
                    offline) the empty plate underneath is what shows, and the label still says
                    which is which. */}
                {hasOpened && MAP_BOX_KEY && (
                  <img
                    src={mapStyleThumbnailUrl(style, MAP_BOX_KEY)}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                )}
                {isActive && (
                  <span className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-secondary shadow">
                    <Check className="h-3 w-3" />
                  </span>
                )}
              </span>
              <span
                className={`text-xs font-semibold ${
                  isActive ? 'text-primary' : 'text-white/70'
                }`}
              >
                {style.label}
              </span>
            </button>
          );
        })}
      </div>
    </aside>
  );
};

export default MapStyleDrawer;
